import { type JSX, useState } from 'react';
import { useNavigate, useLocation, Navigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Icon } from '@/components/icons';
import { LiveDot, Spinner } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { type MembershipsResponse } from '@/hooks/useMemberships';
import { api, ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

interface LocationState {
  from?: { pathname?: string };
}

const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
  remember: z.boolean().optional(),
});

type LoginFormValues = z.infer<typeof loginSchema>;

const SUPPORTED_LANGS = ['es', 'en'] as const;
type SupportedLang = (typeof SUPPORTED_LANGS)[number];

export default function LoginPage(): JSX.Element {
  const { t, i18n } = useTranslation('default');
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoggingIn, isAuthenticated, isLoading } = useAuth();

  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<LoginFormValues>({
    mode: 'onSubmit',
    defaultValues: { email: '', password: '', remember: true },
  });

  if (!isLoading && isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);

    const parsed = loginSchema.safeParse(values);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const path = issue.path[0];
        if (path === 'email' || path === 'password') {
          setError(path, { type: 'validate', message: issue.message });
        }
      }
      return;
    }

    try {
      await login({ email: parsed.data.email, password: parsed.data.password });
      const state = (location.state ?? null) as LocationState | null;
      const redirectTo = state?.from?.pathname ?? '/dashboard';

      try {
        const memberships = await api.get<MembershipsResponse>('/v1/auth/memberships');
        if (memberships.items.length > 1) {
          navigate('/select-workspace', { replace: true });
          return;
        }
      } catch {
        // Swallow — proceed to default redirect below.
      }
      navigate(redirectTo, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && (err.code === 'invalid_credentials' || err.status === 401)) {
        setSubmitError(t('err_credentials'));
      } else if (err instanceof ApiError && err.code === 'tenant_suspended') {
        setSubmitError(err.message);
      } else if (err instanceof Error) {
        setSubmitError(err.message);
      } else {
        setSubmitError(t('err_credentials'));
      }
    }
  });

  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'es').slice(0, 2) as SupportedLang;
  const hasFieldError = Boolean(errors.email || errors.password);
  const showError = submitError !== null || hasFieldError;
  const errorMessage = submitError ?? (hasFieldError ? t('err_credentials') : null);

  const switchLang = (next: SupportedLang) => {
    if (next === lang) return;
    void i18n.changeLanguage(next);
    try {
      localStorage.setItem('i18nextLng', next);
    } catch {
      /* private mode / quota — non-fatal */
    }
  };

  return (
    <div className="relative w-full h-screen overflow-hidden">
      {/* Background photo — bundled at frontend/public/login-bg.jpg.
          Blur + scale hides the soft blur edges. The overlay above
          softens the photo so the white glass card stays legible
          without losing the photographic depth. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-center bg-cover"
        style={{
          backgroundImage: "url('/login-bg.jpg')",
          filter: 'blur(2px)',
          transform: 'scale(1.04)',
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 65% 70% at 50% 50%, rgba(255,255,255,0.30) 0%, rgba(255,255,255,0.55) 100%), linear-gradient(180deg, rgba(20,83,45,0.06) 0%, rgba(255,255,255,0.4) 100%)',
        }}
      />

      {/* Top bar — brand left, language switcher right. Both fixed so
          they don't scroll on tiny viewports. */}
      <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-7 py-5 pointer-events-none">
        <div className="inline-flex items-center gap-2.5 pointer-events-auto">
          <svg width={32} height={32} viewBox="0 0 64 64" fill="none" aria-label="EzSender">
            <circle cx="32" cy="32" r="30" fill="#1a1a1a" />
            <rect x="14" y="14" width="36" height="36" rx="8" fill="#4ade80" />
            <path
              d="M22 38 L42 26 M42 26 L36 26 M42 26 L42 32"
              stroke="#14532d"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span
            className="text-[19px] font-semibold tracking-[-0.02em] text-text"
            style={{ textShadow: '0 1px 2px rgba(255,255,255,0.5)' }}
          >
            ezsender
          </span>
        </div>

        <div
          role="tablist"
          aria-label="Language"
          className="inline-flex p-[3px] rounded-pill border border-line-strong pointer-events-auto"
          style={{
            background: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
        >
          {SUPPORTED_LANGS.map((code) => (
            <button
              key={code}
              type="button"
              role="tab"
              aria-selected={lang === code}
              onClick={() => switchLang(code)}
              className={cn(
                'px-3.5 py-[5px] rounded-pill text-[12px] font-semibold tracking-wider uppercase transition-colors',
                lang === code
                  ? 'bg-ink text-white'
                  : 'bg-transparent text-text-3 hover:text-text',
              )}
            >
              {code}
            </button>
          ))}
        </div>
      </div>

      {/* Centered glass card */}
      <div className="relative z-[2] min-h-screen grid place-items-center px-6">
        <div
          className="w-full max-w-[420px] px-9 pt-10 pb-7 rounded-2xl border"
          style={{
            background: 'rgba(255, 255, 255, 0.82)',
            backdropFilter: 'blur(28px) saturate(140%)',
            WebkitBackdropFilter: 'blur(28px) saturate(140%)',
            borderColor: 'rgba(255, 255, 255, 0.6)',
            boxShadow:
              '0 1px 0 rgba(255, 255, 255, 0.6) inset, 0 30px 60px -20px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(74, 222, 128, 0.08)',
          }}
        >
          <h1 className="display text-[28px] font-semibold tracking-[-0.02em] text-text leading-tight m-0">
            {t('welcome')}
          </h1>
          <p className="text-[14px] text-text-3 mt-1.5 mb-7 leading-relaxed">
            {t('sign_in_sub')}
          </p>

          {showError && errorMessage && (
            <div
              role="alert"
              className="flex gap-2.5 items-start px-3.5 py-3 bg-danger-bg border border-danger-bd rounded-md mb-5"
            >
              <Icon.warn
                width="18"
                height="18"
                className="text-danger flex-shrink-0 mt-px"
              />
              <div className="text-[13px] text-danger leading-relaxed">{errorMessage}</div>
            </div>
          )}

          <form onSubmit={onSubmit} noValidate>
            <div className="mb-4">
              <label
                htmlFor="login-email"
                className="block text-[12px] font-medium text-text-2 mb-1.5"
              >
                {t('email')}
              </label>
              <input
                id="login-email"
                type="email"
                autoComplete="email"
                placeholder={lang === 'es' ? 'tu@empresa.com' : 'you@company.com'}
                disabled={isLoggingIn}
                aria-invalid={Boolean(errors.email)}
                className={cn(
                  'w-full px-3.5 py-3 rounded-md text-[14px] text-text font-sans',
                  'border transition-all outline-none',
                  'placeholder:text-text-4',
                  'focus:border-volt-600 focus:bg-white focus:ring-[3px] focus:ring-volt-glow',
                  errors.email ? 'border-danger-bd' : 'border-line-strong',
                )}
                style={{ background: 'rgba(255, 255, 255, 0.65)' }}
                {...register('email')}
              />
            </div>

            <div className="mb-3">
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="login-password" className="text-[12px] font-medium text-text-2">
                  {t('password')}
                </label>
                <Link
                  to="/forgot-password"
                  className="text-[12px] text-volt-600 font-medium hover:underline"
                >
                  {t('forgot')}
                </Link>
              </div>
              <div className="relative">
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  disabled={isLoggingIn}
                  aria-invalid={Boolean(errors.password)}
                  placeholder="••••••••"
                  className={cn(
                    'w-full pl-3.5 pr-20 py-3 rounded-md text-[14px] text-text font-sans',
                    'border transition-all outline-none',
                    'placeholder:text-text-4',
                    'focus:border-volt-600 focus:bg-white focus:ring-[3px] focus:ring-volt-glow',
                    errors.password ? 'border-danger-bd' : 'border-line-strong',
                  )}
                  style={{ background: 'rgba(255, 255, 255, 0.65)' }}
                  {...register('password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 text-[11px] font-semibold tracking-wider uppercase text-text-3 hover:text-volt-600 transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? t('pw_hide') : t('pw_show')}
                </button>
              </div>
            </div>

            <label className="flex items-center gap-2 text-[13px] text-text-2 mt-3.5 mb-5 cursor-pointer select-none">
              <input type="checkbox" className="sr-only peer" {...register('remember')} />
              <span
                aria-hidden="true"
                className={cn(
                  'w-4 h-4 grid place-items-center rounded-xs border transition-colors',
                  'bg-white border-line-strong',
                  'peer-checked:bg-volt peer-checked:border-volt-600',
                )}
              >
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 12 12"
                  fill="none"
                  className="hidden peer-checked:block"
                >
                  <path
                    d="M2.5 6.5L4.8 8.8L9.5 3.5"
                    stroke="var(--volt-ink)"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              {t('remember')}
            </label>

            <button
              type="submit"
              disabled={isLoggingIn}
              className={cn(
                'w-full inline-flex items-center justify-center gap-2 px-4 py-[13px]',
                'rounded-md text-[14px] font-semibold text-volt-ink',
                'transition-all disabled:opacity-50 disabled:cursor-not-allowed',
                'hover:brightness-105 active:translate-y-px',
              )}
              style={{
                background: 'var(--volt)',
                boxShadow: '0 4px 14px rgba(74, 222, 128, 0.4)',
              }}
            >
              {isLoggingIn ? (
                <>
                  <Spinner />
                  <span>{lang === 'es' ? 'Iniciando…' : 'Signing in…'}</span>
                </>
              ) : (
                <>
                  <span>{t('submit')}</span>
                  <Icon.arrowRight width="16" height="16" />
                </>
              )}
            </button>
          </form>

          <div className="flex items-center gap-3 my-5 text-[12px] text-text-4">
            <div className="flex-1 h-px bg-line-strong" />
            <span>{t('or')}</span>
            <div className="flex-1 h-px bg-line-strong" />
          </div>

          {/* TODO(F2.5-C): SSO endpoint */}
          <button
            type="button"
            disabled
            title={lang === 'es' ? 'Próximamente' : 'Coming soon'}
            className={cn(
              'w-full inline-flex items-center justify-center gap-2.5 px-4 py-[13px]',
              'rounded-md text-[14px] font-semibold text-text-2',
              'border border-line-strong transition-all',
              'opacity-55 cursor-not-allowed',
            )}
            style={{ background: 'rgba(255, 255, 255, 0.6)' }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path
                d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {t('sso')}
          </button>

          <p className="text-[13px] text-text-3 text-center mt-5 mb-0">
            {t('no_account')}{' '}
            <a href="#" className="text-volt-600 font-medium hover:underline">
              {t('sign_up')}
            </a>
          </p>

          <div className="flex justify-between items-center mt-6 pt-5 border-t border-line text-[12px] text-text-4">
            <span className="inline-flex items-center gap-1.5">
              <LiveDot className="w-1.5 h-1.5" />
              {t('secure')}
            </span>
            <span className="inline-flex gap-3.5">
              <a href="#" className="hover:text-text">{t('footer_tos')}</a>
              <a href="#" className="hover:text-text">{t('footer_priv')}</a>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
